import FairfieldsBanner from '@propertypal/shared/src/resources/images/pn-devs/fairfields/banner.png';
import GoldenGateBanner from '@propertypal/shared/src/resources/images/pn-devs/golden-gate/banner.png';
import LongrideBanner from '@propertypal/shared/src/resources/images/pn-devs/longridge/banner.png';
import OakwoodBanner from '@propertypal/shared/src/resources/images/pn-devs/oakwood/banner.png';
import RegentParkBanner from '@propertypal/shared/src/resources/images/pn-devs/regent-park/banner.png';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { ImportantPlaceResponse } from '@propertypal/shared/src/types/important-places';
import BroadbandTable from '@propertypal/web-ui/src/broadband/BroadbandTable';
import Button from '@propertypal/web-ui/src/buttons/Button';
import { LeafletMarker } from '@propertypal/web-ui/src/map/LeafletMap';
import ImageModal from '@propertypal/web-ui/src/modal/ImageModal';
import AgentAccreditations from '@propertypal/web-ui/src/property/AgentAccreditations';
import ContactTable from '@propertypal/web-ui/src/property/ContactTable';
import KeyInfoList from '@propertypal/web-ui/src/property/KeyInfoList';
import OtherDevelopments from '@propertypal/web-ui/src/property/OtherDevelopments';
import CrawfordsFarmDev from '@propertypal/web-ui/src/property/pn-dev/CrawfordsFarmDev.pn';
import DrumfordMeadowContent from '@propertypal/web-ui/src/property/pn-dev/DrumfordMeadowContent.pn';
import FairfieldsContent from '@propertypal/web-ui/src/property/pn-dev/FairfieldsContent.pn';
import GoldenGateContent from '@propertypal/web-ui/src/property/pn-dev/GoldenGateContent.pn';
import LongridgeContent from '@propertypal/web-ui/src/property/pn-dev/LongridgeContent.pn';
import MillmountVillageContent from '@propertypal/web-ui/src/property/pn-dev/MillmountVillageContent.pn';
import OakwoodContent from '@propertypal/web-ui/src/property/pn-dev/OakwoodContent.pn';
import RegentParkContent from '@propertypal/web-ui/src/property/pn-dev/RegentParkContent.pn';
import PropertyAgentBar from '@propertypal/web-ui/src/property/PropertyAgentBar';
import PropertyAgentBox, { PropertyAgentBoxProps } from '@propertypal/web-ui/src/property/PropertyAgentBox.pn';
import PropertyDescription from '@propertypal/web-ui/src/property/PropertyDescription';
import PropertyImportantPlaces from '@propertypal/web-ui/src/property/PropertyImportantPlaces.pn';
import PropertyInsurance from '@propertypal/web-ui/src/property/PropertyInsurance.pn';
import PropertySlider, { PropertySliderProps } from '@propertypal/web-ui/src/property/PropertySlider';
import PropertySummary from '@propertypal/web-ui/src/property/PropertySummary.pn';
import PropertyUpgrade from '@propertypal/web-ui/src/property/PropertyUpgrade.pn';
import RelatedLinks from '@propertypal/web-ui/src/property/RelatedLinks';
import ShowHomesBar from '@propertypal/web-ui/src/property/ShowHomesBar';
import SimilarProperties from '@propertypal/web-ui/src/property/SimilarProperties';
import SimilarPropertiesLite from '@propertypal/web-ui/src/property/SimilarPropertiesLite.pn';
import SlideshowLinks, { SlideshowLinksProps } from '@propertypal/web-ui/src/property/SlideshowLinks';
import PropertyBanner from '@propertypal/web-ui/src/property-box/PropertyBanner';
import Breadcrumbs from '@propertypal/web-ui/src/search/Breadcrumbs';
import { SubHeading } from '@propertypal/web-ui/src/typography';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';
import AdBox from '../ads/AdBox';
import { PROPERTY_IMAGE_LOCATOR, PropertyDetailsContentProps, routerUpdate } from './PropertyDetails';
import {
  DesktopAgentBox,
  DetailsContent,
  DetailsContentAdditional,
  DetailsContentMain,
  DetailsWrapper,
  DevDescriptionContainer,
  MobileAgentBox,
  PNDetails,
  PremiumBanner,
  PropertyTypesWrapper,
  StickyAdWrapper,
} from './PropertyDetailsContent.style.pn';
import PropertyPdfBar from './PropertyPdfBar';
import PropertyUnits from './PropertyUnits';

const AD_BOX_SIDE_SIZES = [[300, 250], [300, 600], [160, 600], 'Fluid'];
const AD_BOX_MIDDLE_SIZES = [[320, 100], [300, 250], [336, 280], 'fluid'];

const BrokerCalculator = dynamic(() => import('@propertypal/web-ui/src/property/BrokerCalculator'), { ssr: false });
const LegalFeesCalculatorLite = dynamic(() => import('@propertypal/web-ui/src/property/LegalFeesCalculatorLite.pn'), {
  ssr: false,
});
const BrochureModal = dynamic(() => import('./BrochureModal'), { ssr: false });
const EnquiryModal = dynamic(() => import('./EnquiryModal'), { ssr: false });
const GoogleMap = dynamic(() => import('../map/GoogleMap'), { ssr: false });
const PropertyMapModal = dynamic(() => import('./PropertyMapModal'), { ssr: false });
const PropertySlideshowModal = dynamic(() => import('./PropertySlideshowModal'), { ssr: false });
const ShareModal = dynamic(() => import('./ShareModal'), { ssr: false });
const StampDutyModal = dynamic(() => import('./StampDutyModal'), { ssr: false });
const NotesModal = dynamic(() => import('./NotesModal'), { ssr: false });
const ImportantPlacesModal = dynamic(() => import('./ImportantPlacesModal.pn'), { ssr: false });
const LeafletMap = dynamic(() => import('@propertypal/web-ui/src/map/LeafletMap'), { ssr: false });

interface PropertyDescriptionBoxProps extends Pick<PropertyDetailsContentProps, 'property' | 'hasFeaturedBrochure'> {
  showAll?: boolean;
  images?: PropertyDetailsContentProps['images'];
}

const PropertyDescriptionBox: React.FC<PropertyDescriptionBoxProps> = ({
  property,
  hasFeaturedBrochure,
  showAll,
  images,
}) => {
  const router = useRouter();

  if (property.featuredDevelopmentContent?.hideAdditionalInfo) {
    return null;
  }

  return (
    <div className="pp-description-box" data-testid="pn-description-box">
      <PropertyDescription property={property} images={images || []} showAll={showAll} />

      {hasFeaturedBrochure && (
        <Button
          onClick={() => routerUpdate(router, `${property.path}/brochure/1`)}
          containerStyle={{ maxWidth: 260, margin: '30px 0' }}
          testID="brochure-btn"
        >
          View Brochure
        </Button>
      )}
    </div>
  );
};

interface PropertyTypesProps
  extends Pick<
    PropertyDetailsContentProps,
    'property' | 'isFeaturedDevelopment' | 'similarProperties' | 'fetchSimilarProperties' | 'onFavourite'
  > {
  isCrawfordsFarm: boolean;
}

const PropertyTypes: React.FC<PropertyTypesProps> = ({
  property,
  isCrawfordsFarm,
  isFeaturedDevelopment,
  similarProperties,
  fetchSimilarProperties,
  onFavourite,
}) => {
  return (
    <PropertyTypesWrapper $isCrawfordsFarm={isCrawfordsFarm}>
      {property.properties && property.published && (
        <SimilarProperties
          properties={property.properties}
          imageLocator={PROPERTY_IMAGE_LOCATOR[1]}
          title="Property Types"
          subTitle={`(${property.totalAvailableTypes} available)`}
          hideBadge
          wrapOnDesktop
          cols={4}
        />
      )}

      {property.type === 'property' && property.development && isFeaturedDevelopment && (
        <SimilarProperties
          properties={similarProperties}
          onInView={fetchSimilarProperties}
          title="More from this development"
          hideOnEmpty
          onFavourite={onFavourite}
          hideBadge
          wrapOnDesktop
          cols={4}
        />
      )}
    </PropertyTypesWrapper>
  );
};

interface PropertyLocationProps
  extends Pick<PropertyDetailsContentProps, 'property' | 'showMapModal' | 'updateSectionUrl' | 'loggedIn'> {
  importantPlaces?: ImportantPlaceResponse[];
  showImportantPlacesModal: boolean;
  titleSuffix?: string;
  marginTop?: number;
}

const PropertyLocation: React.FC<PropertyLocationProps> = ({
  property,
  showMapModal,
  updateSectionUrl,
  importantPlaces,
  showImportantPlacesModal,
  loggedIn,
  titleSuffix,
  marginTop = 60,
}) => {
  const leaftletMarkers = useMemo(() => {
    if (importantPlaces?.length) {
      const markers: LeafletMarker[] = importantPlaces.map((p) => ({
        lat: p.coordinate.latitude,
        lng: p.coordinate.longitude,
        name: p.name,
      }));

      return markers;
    }
    return undefined;
  }, [importantPlaces]);

  return (
    <>
      {!showMapModal && (
        <div style={{ marginTop, overflow: 'hidden' }} className="hide-print pp-property-summary">
          <SubHeading fontSize={20} mb={10}>
            Location{titleSuffix && ` ${titleSuffix}`}
          </SubHeading>
          <LeafletMap
            lat={property.coordinate?.latitude}
            lng={property.coordinate?.longitude}
            zoom={property.coordinate?.mapZoomLevel}
            onExpand={() => updateSectionUrl('/map')}
            markers={leaftletMarkers}
          />
        </div>
      )}

      <PropertyImportantPlaces
        importantPlaces={importantPlaces}
        showImportantPlacesModal={showImportantPlacesModal}
        loggedIn={loggedIn}
        propertyPath={property.path}
      />
    </>
  );
};

const PropertyDetailsContentPN: React.FC<PropertyDetailsContentProps> = ({
  property,
  loggedIn,
  adSuffix,
  images,
  updateSectionUrl,
  imageIndex,
  updatePhotoUrl,
  handleImageClick,
  showSlideshowModal,
  getReplaceUrl,
  hasSitemap,
  hasFeaturedBrochure,
  urlParts,
  favourited,
  toggleFavourited,
  leadId,
  handleInfoClick,
  buyingStatus,
  callAgent,
  modal,
  showPhoneModal,
  ukResident,
  mortgageCalculator,
  onFavourite,
  handleShare,
  isFeaturedDevelopment,
  similarProperties,
  fetchSimilarProperties,
  showPromoteText,
  showMapModal,
  showLegalFees,
  setEnquiryAccountNumber,
  otherDevelopments,
  fetchOtherDevelopments,
  modalImage,
  setModalImage,
  showStampDutyModal,
  setBuyingStatus,
  setUkResident,
  handleBack,
  showEnquiryModal,
  showRegisterInterestModal,
  showRequestValuationModal,
  enquiryAccountNumber,
  setLeadId,
  showBrochureModal,
  showShareModal,
  isAdObscuringModalOpen,
}) => {
  const router = useRouter();

  const isCrawfordsFarm = property.pathId === `d2989`;
  const isDrumfordMeadow = property.pathId === `d3467`;
  const isGoldenGate = property.pathId === `d1498`;
  const isLongridge = property.pathId === `d1741`;
  const isOakwood = property.pathId === `d2000`;
  const isMillmountVillage = property.pathId === `d1291`;
  const isFairfields = property.pathId === `d1579`;
  const isRegentPark = property.pathId === `d3582`;

  const isPremiumDev =
    isCrawfordsFarm ||
    isDrumfordMeadow ||
    isGoldenGate ||
    isLongridge ||
    isOakwood ||
    isMillmountVillage ||
    isFairfields ||
    isRegentPark;

  const [revealNumber, setRevealNumber] = useState<string | null>(null);
  const [importantPlaces, setImportantPlaces] = useState<ImportantPlaceResponse[] | undefined>(
    property.importantPlaces,
  );

  const showNotesModal = !!(loggedIn && urlParts.includes('notes') && property.published);
  const showImportantPlacesModal =
    loggedIn && urlParts.includes('important-places') && (importantPlaces?.length || 0) < 5;

  const agentBoxProps: PropertyAgentBoxProps = {
    property,
    onCallClick: (location) =>
      callAgent(property, undefined, undefined, property.type === 'development' ? 'DDP' : 'PDP', location),
    onShowNumberClick: (location) => {
      const contacts = property?.contacts?.all;
      const items = contacts?.filter((c) => c.phoneNumber?.displayNumber);
      if (items?.length === 1) setRevealNumber(items[0].phoneNumber!.displayNumber);
      showPhoneModal(
        property,
        undefined,
        undefined,
        property.type === 'development' ? 'DDP' : 'PDP',
        location,
        items?.length === 1,
      );
    },
    onEnquiryClick: () => updateSectionUrl('/enquiry'),
    onRegisterInterestClick: () => updateSectionUrl('/register-interest'),
    onRequestValuationClick: () => updateSectionUrl('/request-valuation'),
    registerInterest: property.registerInterest,
    requestValuation: property.requestValuation,
    leadId,
    revealNumber,
  };

  const sliderProps = useMemo(() => {
    if (images.length > 0) {
      const sProps: Omit<PropertySliderProps, 'maxHeight'> = {
        alt: property.addressLine1,
        images,
        imageIndex,
        setImageIndex: updatePhotoUrl,
        onClick: handleImageClick,
        onCounterClick: handleImageClick,
        altCounter: true,
        renderImageOverlay: () => <PropertyBanner property={property} disableFeatured />,
        enableKeyboardControls: !showSlideshowModal,
        updateOnIndexChange: !showSlideshowModal,
      };

      return sProps;
    }
    return null;
  }, [
    property.addressLine1,
    images,
    imageIndex,
    updatePhotoUrl,
    handleImageClick,
    handleImageClick,
    showSlideshowModal,
  ]);

  const slideshowLinksProps: SlideshowLinksProps = {
    getReplaceUrl,
    property,
    hasSitemap,
    hasFeaturedBrochure,
  };

  const locationProps: PropertyLocationProps = {
    importantPlaces,
    loggedIn,
    property,
    showImportantPlacesModal,
    showMapModal,
    updateSectionUrl,
  };

  const propertyTypesProps: PropertyTypesProps = {
    property,
    isFeaturedDevelopment,
    similarProperties,
    fetchSimilarProperties,
    onFavourite,
    isCrawfordsFarm,
  };

  return (
    <PNDetails className="pp-details-page" data-testid="pp-details-page">
      <Breadcrumbs crumbs={property.breadcrumbs} suffix="to Results" arrow path={property.path} />

      <PropertyAgentBar property={property} hideOnMobile />

      {property.showHomeOpeningTime && <ShowHomesBar property={property} />}

      {isCrawfordsFarm && (
        <CrawfordsFarmDev
          slideshowLinksProps={slideshowLinksProps}
          sliderProps={sliderProps}
          descriptionBox={
            <PropertyDescriptionBox property={property} hasFeaturedBrochure={hasFeaturedBrochure} showAll />
          }
          propertyTypes={<PropertyTypes {...propertyTypesProps} />}
          locationDetails={<PropertyLocation {...locationProps} titleSuffix="of Crawfords Farm" marginTop={0} />}
          contactDetails={<PropertyAgentBox {...agentBoxProps} />}
        />
      )}

      {!isCrawfordsFarm && property.coordinate && !urlParts.includes('map') && images.length === 0 && (
        <GoogleMap
          width="100%"
          height={300}
          lat={property.coordinate.latitude}
          lng={property.coordinate.longitude}
          zoom={property.coordinate.mapZoomLevel}
          markers={[
            {
              id: 1,
              lat: property.coordinate.latitude,
              lng: property.coordinate.longitude,
            },
          ]}
        />
      )}

      {sliderProps && !isCrawfordsFarm && <PropertySlider {...sliderProps} maxHeight="65vh" />}

      {!isCrawfordsFarm && <SlideshowLinks {...slideshowLinksProps} />}

      {!isCrawfordsFarm && (
        <DetailsContent className="pp-top-section" data-testid="pp-top-section">
          <DetailsContentMain>
            <DetailsWrapper>
              {isLongridge && (
                <PremiumBanner data-testid="premium-dev-banner" $bg="#fce9f2">
                  <img src={LongrideBanner.src} alt={property.displayAddress} />
                </PremiumBanner>
              )}
              {isOakwood && (
                <PremiumBanner data-testid="premium-dev-banner" $bg="#dd0">
                  <img src={OakwoodBanner.src} alt={property.displayAddress} />
                </PremiumBanner>
              )}
              {isFairfields && (
                <PremiumBanner data-testid="premium-dev-banner" $bg="#bc095c">
                  <img src={FairfieldsBanner.src} alt={property.displayAddress} />
                </PremiumBanner>
              )}
              {isRegentPark && (
                <PremiumBanner data-testid="premium-dev-banner" $bg="#ffd500">
                  <img src={RegentParkBanner.src} alt={property.displayAddress} />
                </PremiumBanner>
              )}
              {isGoldenGate && (
                <PremiumBanner data-testid="premium-dev-banner">
                  <img src={GoldenGateBanner.src} alt={property.displayAddress} />
                </PremiumBanner>
              )}
              <div className="pp-summary-box">
                <PropertySummary
                  property={property}
                  favourited={favourited}
                  toggleFavourited={toggleFavourited}
                  onShare={handleShare}
                  loggedIn={loggedIn}
                  hasFeaturedBrochure={hasFeaturedBrochure}
                  showNotesModal={showNotesModal}
                />

                <MobileAgentBox>
                  <PropertyAgentBox {...agentBoxProps} />
                  {showPromoteText && (
                    <PropertyUpgrade pathId={property.pathId} isRent={property.saleType.key === 'rent'} box />
                  )}
                </MobileAgentBox>

                {property.keyInfo && (
                  <KeyInfoList
                    property={property}
                    onClick={handleInfoClick}
                    buyingStatus={buyingStatus}
                    ukResident={ukResident}
                    mortgageCalculator={mortgageCalculator}
                    small
                  />
                )}

                {property.units && (
                  <PropertyUnits
                    property={property}
                    hasSitemap={hasSitemap}
                    onSiteMapClick={(unit) => updateSectionUrl(`/sitemap/${unit.name}`)}
                  />
                )}
              </div>

              <AdBox
                id={`PDPmiddle_mobile/PDPmiddle_mobile_${adSuffix}`}
                config="PDPmiddle_mobile"
                sizes={AD_BOX_MIDDLE_SIZES}
                hideOver={780}
                obscured={isAdObscuringModalOpen}
                containerStyle={{
                  marginTop: 25,
                  marginBottom: 45,
                  paddingTop: 35,
                  paddingBottom: 35,
                  background: '#f5f5f5',
                }}
                declaration
              />

              {(!isFeaturedDevelopment || property.type !== 'development') && (
                <PropertyDescriptionBox property={property} hasFeaturedBrochure={hasFeaturedBrochure} />
              )}

              <PropertyLocation {...locationProps} />

              {(!!property.account?.accreditations?.length || !!property.account.missingAccreditations?.length) &&
                property.type === 'property' &&
                !property.featuredDevelopmentContent && (
                  <>
                    <SubHeading mt={50}>Agent Accreditations</SubHeading>
                    <AgentAccreditations
                      accreditations={property.account.accreditations}
                      missingAccreditations={property.account.missingAccreditations}
                    />
                  </>
                )}

              <div id="broadband-table" />

              {property.ofcomBroadband && (
                <div className="hide-print">
                  <BroadbandTable displayAddress={property.addressLine1} broadband={property.ofcomBroadband} />
                </div>
              )}

              {mortgageCalculator.hasMortgageCalculator && (
                <>
                  <div id="calculator-marker" />

                  <div className="hide-print">
                    <BrokerCalculator property={property} mortgageCalculator={mortgageCalculator} />
                  </div>
                </>
              )}

              {showLegalFees && (
                <div className="hide-print">
                  <LegalFeesCalculatorLite
                    onCalculateClick={() => {
                      trackGaEvent('legal_fees_click', { page: 'PDP', location: 'Legal Fees Ad' });
                    }}
                  />
                </div>
              )}

              {/* {shouldDisplayGuides(property) && (
              <div
                className="hide-print"
              >
                <ContentHubGuidesPreview />
              </div>
            )} */}

              {!isFeaturedDevelopment && (
                <>
                  <div className="hide-print">
                    <PropertyInsurance isSale={property.status?.key === 'forSale'} />
                  </div>

                  {showPromoteText && (
                    <div className="hide-print">
                      <PropertyUpgrade pathId={property.pathId} isRent={property.saleType.key === 'rent'} />
                    </div>
                  )}
                </>
              )}

              <PropertyPdfBar
                property={property}
                onItemClick={(path) => routerUpdate(router, `${property.path}${path}`)}
                header="Downloads"
              />

              <DesktopAgentBox>
                <RelatedLinks property={property} />
              </DesktopAgentBox>
            </DetailsWrapper>
          </DetailsContentMain>
          <DetailsContentAdditional>
            <DetailsWrapper>
              <DesktopAgentBox>
                <PropertyAgentBox {...agentBoxProps} />
                {showPromoteText && (
                  <PropertyUpgrade pathId={property.pathId} isRent={property.saleType.key === 'rent'} box />
                )}
              </DesktopAgentBox>

              {property.type === 'property' && !property.development && (
                <SimilarPropertiesLite properties={similarProperties} onInView={fetchSimilarProperties} />
              )}

              <MobileAgentBox>
                <RelatedLinks property={property} />
              </MobileAgentBox>

              <StickyAdWrapper>
                <AdBox
                  id={`PDPright_desktop_${adSuffix}`}
                  config="PDPright_desktop"
                  sizes={AD_BOX_SIDE_SIZES}
                  hideUnder={980}
                  obscured={isAdObscuringModalOpen}
                  containerStyle={{
                    marginTop: 25,
                    marginBottom: 45,
                    paddingTop: 35,
                    paddingBottom: 35,
                    background: '#f5f5f5',
                  }}
                  declaration
                />
              </StickyAdWrapper>
            </DetailsWrapper>
          </DetailsContentAdditional>
        </DetailsContent>
      )}

      {showNotesModal && <NotesModal show={showNotesModal} onClose={handleBack} property={property} />}

      {showImportantPlacesModal && (
        <ImportantPlacesModal
          show={showImportantPlacesModal}
          onClose={handleBack}
          importantPlaces={importantPlaces}
          coordinate={property.coordinate}
          onSubmit={(ip) => {
            setImportantPlaces(ip);
            handleBack();
          }}
          propertyId={property.id}
        />
      )}

      <ImageModal
        ariaLabel="EPC image popup"
        show={!!modalImage}
        onClose={() => setModalImage('')}
        imageUrl={modalImage}
      />

      {showStampDutyModal && (
        <StampDutyModal
          buyingStatus={buyingStatus}
          setBuyingStatus={setBuyingStatus}
          ukResident={ukResident}
          setUkResident={setUkResident}
          property={property}
          show={showStampDutyModal}
          onClose={handleBack}
        />
      )}

      {showMapModal && (
        <PropertyMapModal
          show={showMapModal}
          property={property}
          onClose={handleBack}
          adSuffix={adSuffix}
          hideSchoolData
          importantPlaces={importantPlaces}
        />
      )}

      {showSlideshowModal && (
        <PropertySlideshowModal
          imageIndex={imageIndex}
          setImageIndex={updatePhotoUrl}
          show={showSlideshowModal}
          property={property}
          onClose={handleBack}
          onTabClick={(tab, index) => updateSectionUrl(`/${tab}`, index)}
          url={router.asPath}
          imageLocator={PROPERTY_IMAGE_LOCATOR[0]}
          hasSitemap={hasSitemap}
          adSuffix={adSuffix}
        />
      )}

      {(showEnquiryModal || showRegisterInterestModal || showRequestValuationModal) && (
        <EnquiryModal
          accountNumber={enquiryAccountNumber}
          show={showEnquiryModal || showRegisterInterestModal || showRequestValuationModal}
          onClose={handleBack}
          property={property}
          registerInterestModal={showRegisterInterestModal}
          requestValuationModal={showRequestValuationModal}
          onSuccess={setLeadId}
          loggedIn={loggedIn}
          loggedInUser={property.loggedInUser}
        />
      )}

      {showBrochureModal && <BrochureModal show={showBrochureModal} onClose={handleBack} property={property} />}

      {showShareModal && (
        <ShareModal show={router.asPath.includes('/share')} onClose={handleBack} property={property} />
      )}

      {modal}

      {!isCrawfordsFarm && <PropertyTypes {...propertyTypesProps} />}

      {isFeaturedDevelopment && !isPremiumDev && property.type === 'development' && (
        <DevDescriptionContainer>
          <PropertyDescriptionBox
            property={property}
            hasFeaturedBrochure={hasFeaturedBrochure}
            showAll
            images={images}
          />
        </DevDescriptionContainer>
      )}

      {isDrumfordMeadow && <DrumfordMeadowContent />}
      {isGoldenGate && <GoldenGateContent />}
      {isLongridge && <LongridgeContent />}
      {isOakwood && <OakwoodContent />}
      {isMillmountVillage && <MillmountVillageContent />}
      {isFairfields && <FairfieldsContent />}
      {isRegentPark && <RegentParkContent />}

      {property.type === 'development' && (
        <OtherDevelopments data={otherDevelopments} onInView={fetchOtherDevelopments} small />
      )}

      {(property.type === 'development' || property.development) &&
        property.contacts &&
        property.contacts.all.length > 1 &&
        !property.featuredDevelopmentContent?.hideAgentContact &&
        property.published && (
          <div className="pp-contact-box hide-print">
            <ContactTable
              property={property}
              onEnquiryClick={(contact) => {
                trackGaEvent('enquiry_click', {
                  location: 'Contact Table',
                  account_number: contact.accountNumber,
                  page: 'DDP',
                });
                setEnquiryAccountNumber(contact.accountNumber);
                updateSectionUrl('/enquiry');
              }}
              onRegisterInterestClick={(contact) => {
                setEnquiryAccountNumber(contact.accountNumber);
                updateSectionUrl('/register-interest');
              }}
              registerInterest={property.registerInterest}
            />
          </div>
        )}
    </PNDetails>
  );
};

export default PropertyDetailsContentPN;
